/**
 * CostForm.js — Type-specific form for editing a cost
 *
 * Renders fields appropriate to the cost type:
 * mark_stress, spend_hope, spend_token, spend_hp, set_cooldown
 */

import { useId } from 'react';
import { VALID_COST_TYPES } from '../../../../engine/schema';
import { cn } from '../../../../lib/utils';

const inputClass = cn(
  'bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white text-sm',
  'focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none w-full'
);

const labelClass = 'text-sm font-medium text-gray-300 mb-1 block';

const CostForm = ({ cost, onUpdate }) => {
  const baseId = useId();
  const handleTypeChange = (newType) => {
    const base = { type: newType };
    switch (newType) {
      case 'mark_stress': onUpdate({ ...base, amount: 1 }); break;
      case 'spend_hope': onUpdate({ ...base, amount: 1 }); break;
      case 'spend_token': onUpdate({ ...base, tokenId: '', amount: 1 }); break;
      case 'spend_hp': onUpdate({ ...base, amount: 1 }); break;
      case 'set_cooldown': onUpdate({ ...base, cooldownKey: '', duration: 1 }); break;
      default: onUpdate(base);
    }
  };

  const renderFields = () => {
    switch (cost.type) {
      case 'mark_stress':
        return (
          <div>
            <label htmlFor={`${baseId}-stress-amount`} className={labelClass}>Amount</label>
            <input
              id={`${baseId}-stress-amount`}
              type="number"
              min={1}
              value={cost.amount ?? 1}
              onChange={(e) => onUpdate({ ...cost, amount: Number(e.target.value) })}
              className={inputClass}
            />
          </div>
        );
      case 'spend_hope':
        return (
          <div>
            <label htmlFor={`${baseId}-hope-amount`} className={labelClass}>Amount</label>
            <input
              id={`${baseId}-hope-amount`}
              type="number"
              min={1}
              value={cost.amount ?? 1}
              onChange={(e) => onUpdate({ ...cost, amount: Number(e.target.value) })}
              className={inputClass}
            />
          </div>
        );
      case 'spend_token':
        return (
          <div className="space-y-2">
            <div>
              <label htmlFor={`${baseId}-token-id`} className={labelClass}>Token ID</label>
              <input
                id={`${baseId}-token-id`}
                type="text"
                value={cost.tokenId || ''}
                onChange={(e) => onUpdate({ ...cost, tokenId: e.target.value })}
                placeholder="e.g. rage-tokens"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor={`${baseId}-token-amount`} className={labelClass}>Amount</label>
              <input
                id={`${baseId}-token-amount`}
                type="number"
                min={1}
                value={cost.amount ?? 1}
                onChange={(e) => onUpdate({ ...cost, amount: Number(e.target.value) })}
                className={inputClass}
              />
            </div>
          </div>
        );
      case 'spend_hp':
        return (
          <div>
            <label htmlFor={`${baseId}-hp-amount`} className={labelClass}>Amount</label>
            <input
              id={`${baseId}-hp-amount`}
              type="number"
              min={1}
              value={cost.amount ?? 1}
              onChange={(e) => onUpdate({ ...cost, amount: Number(e.target.value) })}
              className={inputClass}
            />
          </div>
        );
      case 'set_cooldown':
        return (
          <div className="space-y-2">
            <div>
              <label htmlFor={`${baseId}-cooldown-key`} className={labelClass}>Cooldown Key</label>
              <input
                id={`${baseId}-cooldown-key`}
                type="text"
                value={cost.cooldownKey || ''}
                onChange={(e) => onUpdate({ ...cost, cooldownKey: e.target.value })}
                placeholder="e.g. ability-cooldown"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor={`${baseId}-duration`} className={labelClass}>Duration (turns)</label>
              <input
                id={`${baseId}-duration`}
                type="number"
                min={1}
                value={cost.duration ?? 1}
                onChange={(e) => onUpdate({ ...cost, duration: Number(e.target.value) })}
                className={inputClass}
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor={`${baseId}-type`} className={labelClass}>Cost Type</label>
        <select
          id={`${baseId}-type`}
          value={cost.type || ''}
          onChange={(e) => handleTypeChange(e.target.value)}
          className={inputClass}
        >
          {VALID_COST_TYPES.map((t) => (
            <option key={t} value={t}>
              {t.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </div>
      {renderFields()}
    </div>
  );
};

export default CostForm;
